import React, { FC, useEffect } from 'react';
import { Formik, Form } from 'formik';
import {
  FormValues,
  initialOrganizationValues,
  orgBoundaryOptions,
  organizationalBoundary,
} from '../../companySetupConstants';
import COSelect from 'shared/components/COSelect';
import {
  FormFieldLabels,
  FormFieldNames,
  FormFieldPlaceholders,
} from 'enums/OrgFormEnums';
import InputField from 'shared/components/InputField';
import COButton from 'shared/components/COButton';
import { ButtonLabels } from 'enums/COPopoverEnums';
import styles from '../../organisation.module.scss';
import { orgValidationSchema } from '../../validation';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import { convertToOptions } from 'shared/utils/convertOptions';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import MetadataService from 'services/ParentCompanySetup/metadata.service';
interface OrganizationDetailsFormProps {
  handleSubmit: (values: FormValues) => void;
  handleCancelModal: () => void;
  handleToolTipOpen?: (fieldName?: HelpDeskInputFieldEnum) => () => void;
}

const OrganizationDetailsForm: FC<OrganizationDetailsFormProps> = ({
  handleSubmit,
  handleCancelModal,
  handleToolTipOpen,
}) => {
  const { organizationData } = useParentOrganizationContext();
  const { sectorMetaData, fetchSector } = ParentCompanySetupService();
  const {
    sizeMetaData,
    fetchSizeMetaData,
    fetchCompanyTypesMeta,
    companyTypesMeta,
  } = MetadataService();
  useEffect(() => {
    fetchSizeMetaData();
    fetchCompanyTypesMeta();
  }, []);

  const initVal = {
    ...initialOrganizationValues,
    organizationName: organizationData?.company?.name || '',
  };

  const handleCompanyTypesChange =
    (setFieldValue: Function) => async (value: number) => {
      setFieldValue(FormFieldNames.COMPANY_TYPE, value);
      if (value) setFieldValue(FormFieldNames.SECTOR, null);
      await fetchSector(value);
    };

  const {
    organizationName,
    organizationSize,
    organizationSector,
    organizationCompanyType,
    organizationBoundary,
  } = HelpDeskInputFieldEnum;

  return (
    <Formik
      initialValues={initVal}
      validationSchema={orgValidationSchema}
      onSubmit={handleSubmit}>
      {({ handleChange, values, setFieldValue }) => (
        <Form>
          <div className={styles.formWrapper}>
            <div className={styles.fieldContainer}>
              <InputField
                name={FormFieldNames.ORGANIZATION_NAME}
                label={FormFieldLabels.ORGANIZATION_NAME}
                placeholderstring={FormFieldPlaceholders.ORGANIZATION_NAME}
                onChange={handleChange}
                info
                infoClick={handleToolTipOpen?.(organizationName)}
              />
            </div>

            <div className={styles.fieldContainer}>
              <COSelect
                allowClear={false}
                name={FormFieldNames.ORGANIZATION_SIZE}
                label={FormFieldLabels.ORGANIZATION_SIZE}
                placeholderstring={FormFieldPlaceholders.ORGANIZATION_SIZE}
                options={convertToOptions(sizeMetaData)}
                onChange={handleChange}
                info
                infoClick={handleToolTipOpen?.(organizationSize)}
              />
            </div>
            <div className={styles.fieldContainer}>
              <COSelect
                allowClear={false}
                name={FormFieldNames.COMPANY_TYPE}
                label={FormFieldLabels.COMPANY_TYPE}
                placeholderstring={FormFieldPlaceholders.COMPANY_TYPE}
                onChange={handleCompanyTypesChange(setFieldValue)}
                options={convertToOptions(companyTypesMeta)}
                info
                infoClick={handleToolTipOpen?.(organizationCompanyType)}
              />
            </div>

            <div className={styles.fieldContainer}>
              <COSelect
                allowClear={false}
                name={FormFieldNames.SECTOR}
                label={FormFieldLabels.SECTOR}
                placeholderstring={FormFieldPlaceholders.SECTOR}
                options={convertToOptions(sectorMetaData)}
                disabled={!values.companyType}
                info
                infoClick={handleToolTipOpen?.(organizationSector)}
              />
            </div>

            <div className={styles.fieldContainer}>
              <COSelect
                allowClear={false}
                name={FormFieldNames.ORGANIZATIONAL_BOUNDARY}
                label={FormFieldLabels.ORGANIZATIONAL_BOUNDARY}
                placeholderstring={
                  FormFieldPlaceholders.ORGANIZATIONAL_BOUNDARY
                }
                options={orgBoundaryOptions}
                onChange={handleChange}
                info
                infoClick={handleToolTipOpen?.(organizationBoundary)}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <COButton submit>{ButtonLabels.NEXT}</COButton>
            <COButton secondary onClick={handleCancelModal}>
              {ButtonLabels.CANCEL}
            </COButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};
export default OrganizationDetailsForm;
